import { Button, Card, DatePicker, Flex, Form, Input, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { serializeAllowedScopes } from 'src/features/workspaces/components/agents/scopeModel'
import { useShowApiErrors } from 'src/utils'

import { useCreateMcpPersonalToken } from './hooks'
import { isPatExpiryAllowed, patExpiryInstant } from './model'
import type { McpMemberService } from './types'

const { Text } = Typography

// Field names match Core's so its 400 field errors land on the inputs.
type FormValues = { name: string; expires_at: Dayjs }

type Props = {
  workspaceId: number
  service: McpMemberService
  /** Picked in the wizard's scope step. */
  scopes: Set<string>
  onIssued: (token: string) => void
}

export const PersonalTokenForm = ({
  workspaceId,
  service,
  scopes,
  onIssued
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const showApiErrors = useShowApiErrors()
  const create = useCreateMcpPersonalToken(workspaceId)

  const handleFinish = async (values: FormValues) => {
    const result = await create
      .mutateAsync({
        organization_id: workspaceId,
        service: service.service,
        name: values.name.trim(),
        scopes: serializeAllowedScopes(scopes),
        expires_at: patExpiryInstant(
          values.expires_at.toDate(),
          new Date()
        ).toISOString()
      })
      .catch((error: unknown) => {
        showApiErrors(error, form)

        return null
      })
    if (result) onIssued(result.token)
  }

  return (
    <Card size="small" title={t('workspace.aiConnectors.pat.title')}>
      <Flex vertical gap={12}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('workspace.aiConnectors.pat.intro')}
        </Text>
        <Form<FormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={handleFinish}
        >
          <Form.Item
            name="name"
            label={t('workspace.aiConnectors.pat.name')}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('workspace.aiConnectors.pat.nameRequired')
              }
            ]}
          >
            <Input
              maxLength={100}
              placeholder={t('workspace.aiConnectors.pat.namePlaceholder')}
            />
          </Form.Item>
          <Form.Item
            name="expires_at"
            label={t('workspace.aiConnectors.pat.expiresAt')}
            rules={[
              {
                required: true,
                message: t('workspace.aiConnectors.pat.expiresRequired')
              }
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={(day) =>
                !isPatExpiryAllowed(day.toDate(), new Date())
              }
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={create.isPending}
            disabled={scopes.size === 0}
          >
            {t('workspace.aiConnectors.pat.issue')}
          </Button>
        </Form>
      </Flex>
    </Card>
  )
}
